import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { CommunitiesRootNodeFormModel } from "./common";
import { CommunityHubSidebarViewModel } from "client/Api/Hub/hubSidebarManagement";
import {
	ActionListItem,
	DropOverActionList,
	IconButton,
	ReorderableList,
	ReorderableListAddButtonClickEventArgs,
	Typography
} from "client/Components";
import { HubSidebarItemContent } from "./HubSidebarItemContent";
import { localize } from "client_legacy/ndaw/culture";
import {
	ensureNotNil,
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import { HubDataSource } from "client/Api/Hub/hub";
import { Coordinate } from "client/Misc/commonTypes";
import {
	AlertManager,
	OpenItemMenuState,
	useAlerts,
	useItemDropOverMenuProps,
	useItemMenuState,
	useUnmountedCancellation
} from "client/Hooks";
import { SaveCommunityResult, useEditCommunityDialog } from "../Communities/EditCommunityDialog/EditCommunityDialog";
import { useCommunityDeletionConfirmationDialog } from "../Communities/CommunityDeletionConfirmationDialog";
import { CommunityKind, deleteCommunity } from "client/Api/Communities/community";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { CommunityEvent, publish } from "../Communities/events";
import { Guid } from "client_legacy/ndaw/guid";
import {
	getClientId,
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";

export type CommunitiesChange = {
	kind: "added";
	clientId: Guid;
}
	| { kind: "deleted" | "updated"; };

export type CommunitiesNodeProps = {
	attachable: IValueInputAttachable<CommunitiesRootNodeFormModel>;
	doesMatchSearchTerm: (item: CommunityHubSidebarViewModel) => boolean;
	registerDialogWithBackdrop: () => (() => void);
	onCommunitiesChanged: (change: CommunitiesChange) => void;
	nodeClientIdToScrollTo: Guid | null;
	onNodeClientIdToScrollToChange: (value: Guid | null) => void;
};

export const CommunitiesNode = (props: CommunitiesNodeProps): ReactElement => {
	const {
		attachable,
		doesMatchSearchTerm,
		registerDialogWithBackdrop,
		onCommunitiesChanged,
		nodeClientIdToScrollTo,
		onNodeClientIdToScrollToChange
	} = props;

	const [menuPosition, setMenuPosition] = useState<Coordinate | null>(null);

	const addButtonRef: RefObject<HTMLButtonElement | null> = useRef(null);
	const itemMenuButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const [deleteCommunityConfirmationDialog, openDeleteCommunityConfirmationDialog] = useCommunityDeletionConfirmationDialog();
	const [editCommunityDialog, openEditHubCategoryDialog] = useEditCommunityDialog();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const {
		menuState: itemMenuState,
		onOpenMoreMenu: onOpenItemMoreMenu,
		onOpenContextMenu: onOpenItemContextMenu,
		onCloseMenu: onCloseItemMenu
	} = useItemMenuState();

	const hasDialogWithBackdrop: boolean = isNotNil(editCommunityDialog);

	useEffect(
		(): (() => void) | void => {
			if (!hasDialogWithBackdrop) {
				return;
			}

			return registerDialogWithBackdrop();
		},
		[hasDialogWithBackdrop, registerDialogWithBackdrop]
	);

	const communities: readonly ObjectWithClientId<CommunityHubSidebarViewModel>[] = attachable.value
		.children;

	const childrenAttachable: IValueInputAttachable<readonly ObjectWithClientId<CommunityHubSidebarViewModel>[]> = attachable
		.for("children");

	const setCommunities = useCallback(
		(value: readonly ObjectWithClientId<CommunityHubSidebarViewModel>[], change: CommunitiesChange): void => {
			childrenAttachable.reducer
				.setValue(value);

			childrenAttachable.validation
				.triggerInputFinished();

			onCommunitiesChanged(change);
		},
		[
			childrenAttachable.reducer,
			childrenAttachable.validation,
			onCommunitiesChanged
		]
	);

	const createCommunityHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<SaveCommunityResult> = await openEditHubCategoryDialog({});

			if (!result.wasSuccessful) {
				return;
			}

			const { id, name } = result.result;

			const community: ObjectWithClientId<CommunityHubSidebarViewModel> = setClientId({
				id,
				name,
				kind: CommunityKind.Custom,
				permissions: {
					canEdit: true,
					canDelete: true
				}
			});

			setCommunities(
				sortCommunities(
					[...communities, community]
				),
				{
					kind: "added",
					clientId: getClientId(community)
				}
			);
		},
		[
			communities,
			openEditHubCategoryDialog,
			setCommunities
		]
	);

	const editHandler = useCallback(
		async (id: number): Promise<void> => {
			const result: TryResult<SaveCommunityResult> = await openEditHubCategoryDialog({
				communityId: id
			});

			if (!result.wasSuccessful) {
				return;
			}

			setCommunities(
				sortCommunities(
					communities.map((x): ObjectWithClientId<CommunityHubSidebarViewModel> => {
						if (x.id === id) {
							return {
								...x,
								name: result.result
									.name
							};
						}

						return x;
					})
				),
				{ kind: "updated" }
			);
		},
		[
			communities,
			openEditHubCategoryDialog,
			setCommunities
		]
	);

	const deleteHandler = useCallback(
		async (id: number): Promise<void> => {
			const result: TryResult<void> = await openDeleteCommunityConfirmationDialog();

			if (!result.wasSuccessful) {
				return;
			}

			try {
				await deleteCommunity(id, globalCancellation);

				setCommunities(
					communities.filter((x) => x.id !== id),
					{ kind: "deleted" }
				);

				publish(CommunityEvent.CommunityDeleted, { communityId: id });
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			communities,
			globalCancellation,
			openDeleteCommunityConfirmationDialog,
			setCommunities
		]
	);

	const openMenuHandler = useCallback(
		(args: ReorderableListAddButtonClickEventArgs): void => {
			setMenuPosition({
				x: args.clientX,
				y: args.clientY
			});
		},
		[]
	);

	return (
		<>
			<ReorderableList.Group
				headerContent={(
					<HubSidebarItemContent
						trackingId="content"
						icon={{
							kind: "font",
							name: "users",
							color: "primary"
						}}
					>
						<Typography weight="bold">
							{localize("_Communities")}
						</Typography>
					</HubSidebarItemContent>
				)}
				actions={(
					<IconButton
						ref={itemMenuButtonRef}
						icon="ellipsis"
						kind="transparent"
						shape="round"
						trackingId="open-actions"
						onClick={() => onOpenItemMoreMenu()}
						isActive={itemMenuState.isOpen && !itemMenuState.isContextMenu}
					/>
				)}
				trackingId={generateTrackingId("type-item", `|${HubDataSource.Community}`)}
				mouseInteraction={{
					onContextMenu: onOpenItemContextMenu
				}}
				isActive={itemMenuState.isOpen}
			>
				<ReorderableList
					trackingId="type-item-children-list"
				>
					{communities
						.filter(doesMatchSearchTerm)
						.map((x): ReactElement => {
							const { id } = x;

							return (
								<CommunityNode
									key={id}
									community={x}
									onEdit={() => editHandler(id)}
									onDelete={() => deleteHandler(id)}
									nodeClientIdToScrollTo={nodeClientIdToScrollTo}
									onNodeClientIdToScrollToChange={onNodeClientIdToScrollToChange}
								/>
							);
						})
					}
				</ReorderableList>

				<ReorderableList.AddButton
					ref={addButtonRef}
					onClick={openMenuHandler}
					trackingId="add"
				/>

				{(menuPosition && addButtonRef.current) && (
					<DropOverActionList
						target={addButtonRef.current}
						onClose={() => setMenuPosition(null)}
						contentAlignment="tl"
						targetAlignment={{
							kind: "absolute",
							coordinate: menuPosition
						}}
						menuTitle={localize("_Add")}
						trackingId="communities-actions"
					>
						<ActionListItem
							onClick={() => createCommunityHandler()}
							text={localize("_CreateCommunity")}
							trackingId="create-community"
							icon="note-sticky"
						/>
					</DropOverActionList>
				)}
				{itemMenuState.isOpen && (
					<RootMenu
						target={ensureNotNil(itemMenuButtonRef.current)}
						onClose={onCloseItemMenu}
						menuState={itemMenuState}
						onCreateCommunity={createCommunityHandler}
						trackingId={generateTrackingId("open-actions", "__dropover")}
					/>
				)}

				{editCommunityDialog}
				{deleteCommunityConfirmationDialog}
			</ReorderableList.Group>
		</>
	);
};
CommunitiesNode.displayName = "CommunitiesNode";

type CommunityNodeProps = {
	community: ObjectWithClientId<CommunityHubSidebarViewModel>;
	onEdit: () => void;
	onDelete: () => void;
	nodeClientIdToScrollTo: Guid | null;
	onNodeClientIdToScrollToChange: (value: Guid | null) => void;
};

const CommunityNode = (props: CommunityNodeProps): ReactElement => {
	const {
		community,
		onEdit,
		onDelete,
		nodeClientIdToScrollTo,
		onNodeClientIdToScrollToChange
	} = props;

	const {
		id,
		name,
		permissions: {
			canEdit,
			canDelete
		}
	} = community;

	const itemRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const menuButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const {
		menuState,
		onOpenMoreMenu,
		onOpenContextMenu,
		onCloseMenu
	} = useItemMenuState();

	const clientId: Guid = getClientId(community);

	useEffect(
		(): void => {
			if (nodeClientIdToScrollTo === clientId) {
				itemRef.current
					?.scrollIntoView();

				onNodeClientIdToScrollToChange(null);
			}
		},
		[
			nodeClientIdToScrollTo,
			clientId,
			onNodeClientIdToScrollToChange
		]
	);

	const trackingId: string = generateTrackingId("community", `|${id}`);
	const hasActions: boolean = canEdit || canDelete;

	return (
		<>
			<ReorderableList.RowItem
				trackingId={trackingId}
				actions={
					hasActions
						? (
							<IconButton
								ref={menuButtonRef}
								icon="ellipsis"
								kind="transparent"
								shape="round"
								trackingId={generateTrackingId(trackingId, "__actions")}
								onClick={() => onOpenMoreMenu()}
								isActive={menuState.isOpen && !menuState.isContextMenu}
							/>
						)
						: undefined
				}
				mouseInteraction={{
					onContextMenu: hasActions
						? onOpenContextMenu
						: undefined
				}}
				isActive={hasActions && menuState.isOpen}
			>
				<div ref={itemRef}>
					<HubSidebarItemContent
						trackingId="content"
						icon={{
							kind: "font",
							name: "note-sticky",
							color: "primary"
						}}
					>
						{name}
					</HubSidebarItemContent>
				</div>
			</ReorderableList.RowItem>
			{hasActions && menuState.isOpen && (
				<CommunityMenu
					target={ensureNotNil(menuButtonRef.current)}
					onClose={onCloseMenu}
					menuState={menuState}
					trackingId={generateTrackingId(generateTrackingId(trackingId, "__actions"), "__dropover")}
					name={name}
					onEdit={canEdit ? () => onEdit() : undefined}
					onDelete={canDelete ? () => onDelete() : undefined}
				/>
			)}
		</>
	);
};
CommunityNode.displayName = "CommunityNode";

type CommunityMenuProps = {
	target: HTMLElement;
	onClose: () => void;
	menuState: OpenItemMenuState;
	trackingId: string;
	name: string;
	onEdit: (() => void) | undefined;
	onDelete: (() => void) | undefined;
};

const CommunityMenu = (props: CommunityMenuProps): ReactElement | null => {
	const {
		target,
		menuState,
		onClose,
		trackingId,
		name,
		onEdit,
		onDelete
	} = props;

	const {
		contentAlignment,
		targetAlignment,
		closeOnMouseDown
	} = useItemDropOverMenuProps(
		menuState,
		{
			moreMenuTargetAlignment: "br",
			moreMenuContentAlignment: "tr"
		}
	);

	return (
		<DropOverActionList
			trackingId={trackingId}
			target={target}
			onClose={() => onClose()}
			menuTitle={name}
			targetAlignment={targetAlignment}
			contentAlignment={contentAlignment}
			closeOnMouseDown={closeOnMouseDown}
		>
			{onEdit && (
				<ActionListItem
					onClick={() => onEdit()}
					text={localize("_Edit")}
					trackingId="edit"
					icon="pen"
				/>
			)}

			{onDelete && (
				<ActionListItem
					onClick={() => onDelete()}
					text={localize("_Delete")}
					trackingId="delete-category"
					icon="trash-can"
				/>
			)}
		</DropOverActionList>
	);
};
CommunityMenu.displayName = "CommunityMenu";

type RootMenuProps = {
	target: HTMLElement;
	onClose: () => void;
	menuState: OpenItemMenuState;
	onCreateCommunity: () => void;
	trackingId: string;
};

const RootMenu = (props: RootMenuProps): ReactElement => {
	const {
		target,
		onClose,
		menuState,
		onCreateCommunity,
		trackingId
	} = props;

	const {
		contentAlignment,
		targetAlignment,
		closeOnMouseDown
	} = useItemDropOverMenuProps(
		menuState,
		{
			moreMenuContentAlignment: "tr",
			moreMenuTargetAlignment: "br"
		}
	);

	return (
		<DropOverActionList
			target={target}
			onClose={onClose}
			menuTitle={localize("_Add")}
			targetAlignment={targetAlignment}
			contentAlignment={contentAlignment}
			closeOnMouseDown={closeOnMouseDown}
			trackingId={trackingId}
		>
			<ActionListItem
				onClick={onCreateCommunity}
				text={localize("_CreateCommunity")}
				trackingId="create-community"
				icon="note-sticky"
			/>
		</DropOverActionList>
	);
};
RootMenu.displayName = "RootMenu";

function sortCommunities(
	input: readonly ObjectWithClientId<CommunityHubSidebarViewModel>[]
): readonly ObjectWithClientId<CommunityHubSidebarViewModel>[] {
	return sortBy(
		sortBy(
			input,
			(x) => x.name
		),
		(x) => x.kind === CommunityKind.AllCompany
			? 0
			: 1
	);
}
